import Swal from 'sweetalert2'

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
})

const notificationError = (message, useI18next = false) => {
  if (!message) return
  
  try {
    const messageText = useI18next ? String(message) : message
    
    Toast.fire({
      icon: 'error',
      title: messageText
    })
  } catch (error) {
    // Fallback if Swal fails
    const messageText = useI18next ? String(message) : message
    if (typeof window !== 'undefined' && window?.alert) {
      window.alert(messageText)
    } else {
      console.error(messageText)
    }
  }
}

const messageSuccess = (message, useI18next = false) => {
  if (!message) return
  
  try {
    const messageText = useI18next ? String(message) : message
    
    Toast.fire({
      icon: 'success',
      title: messageText
    })
  } catch (error) {
    // Fallback if Swal fails
    const messageText = useI18next ? String(message) : message
    if (typeof window !== 'undefined' && window?.alert) {
      window.alert(messageText)
    } else {
      console.log(messageText)
    }
  }
}

const generateRandomString = (length = 10) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

const isNullOrEmpty = (value) =>
  value === undefined || value === null || value === '' || value?.length === 0

const removeNullValues = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  const cleaned = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== null && obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
}

/**
 * Form verilerinden sadece dolu olan optional alanları ekler
 * @param {Object} submitData - Gönderilecek veri objesi (mutate edilir)
 * @param {Object} formData - Form verileri
 * @param {Object} fieldConfig - Alan konfigürasyonu
 * @param {string[]} fieldConfig.string - String alanları (trim yapılır)
 * @param {string[]} fieldConfig.number - Number alanları (parseFloat yapılır)
 * @param {string[]} fieldConfig.boolean - Boolean alanları (undefined kontrolü yapılır)
 */
const addOptionalFields = (submitData, formData, fieldConfig = {}) => {
  const { string = [], number = [], boolean = [] } = fieldConfig;

  // String alanları - trim yapıp boş değilse ekle
  string.forEach(fieldName => {
    if (formData[fieldName] && formData[fieldName].trim()) {
      submitData[fieldName] = formData[fieldName].trim();
    }
  });

  // Number alanları - parseFloat yapıp boş değilse ekle
  number.forEach(fieldName => {
    if (formData[fieldName] !== undefined && formData[fieldName] !== null && formData[fieldName] !== '') {
      submitData[fieldName] = parseFloat(formData[fieldName]);
    }
  });

  // Boolean alanları - undefined değilse ekle
  boolean.forEach(fieldName => {
    if (formData[fieldName] !== undefined) {
      submitData[fieldName] = formData[fieldName];
    }
  });

  return submitData;
}

/**
 * Silme işlemi için onay dialog'u gösterir
 * @param {string} itemName - Silinecek öğenin adı (örn: "Üniversite", "Öğrenci")
 * @param {string} itemTitle - Silinecek öğenin başlığı/ismi (örn: "İstanbul Üniversitesi")
 * @returns {Promise<boolean>} - Kullanıcı onayladıysa true, iptal ettiyse false
 */
const confirmDelete = async (itemName, itemTitle) => {
  try {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: `"${itemTitle}" ${itemName.toLowerCase()}ini silmek istediğinize emin misiniz?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'İptal',
      reverseButtons: true
    })

    return result.isConfirmed
  } catch (error) {
    console.error('Confirm delete error:', error)
    // Fallback to native confirm
    return window.confirm(`"${itemTitle}" ${itemName.toLowerCase()}ini silmek istediğinize emin misiniz?`)
  }
}

/**
 * Silme işlemi başarılı olduğunda success mesajı gösterir
 * @param {string} itemName - Silinen öğenin adı (örn: "Üniversite", "Öğrenci")
 * @param {number} timer - Mesajın otomatik kapanma süresi (ms), varsayılan 2000
 */
const showDeleteSuccess = (itemName, timer = 2000) => {
  try {
    Swal.fire({
      title: 'Silindi!',
      text: `${itemName} başarıyla silindi`,
      icon: 'success',
      timer: timer,
      showConfirmButton: false
    })
  } catch (error) {
    // Fallback if Swal fails
    messageSuccess(`${itemName} başarıyla silindi`)
  }
}

/**
 * Tarihi gün/ay/yıl formatında döndürür. Geçersiz ise ham değeri verir.
 * @param {string|Date} value
 * @param {string} locale
 * @returns {string}
 */
const formatDate = (value, locale = 'tr-TR') => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
    .format(date)
    .replace(/\./g, '/')
}

/**
 * Verilen objeden (row) dile göre uygun alanı döndürür.
 * tr -> field (örn: name)
 * en -> fieldEn (örn: nameEn)
 * ru -> fieldRu (örn: nameRu)
 * @param {Object} row - Veri objesi
 * @param {string} field - Ana alan adı (örn: 'name', 'faculty')
 * @param {string} language - Mevcut dil kodu ('tr', 'en', 'ru')
 * @returns {string}
 */
const getLocaleValue = (row, field, language) => {
  if (!row) return '-'
  
  // Dil özelindeki alan adını oluştur (nameEn, nameRu vb.)
  // tr için ek yok
  const suffix = language === 'tr' ? '' : language.charAt(0).toUpperCase() + language.slice(1)
  const localizedKey = `${field}${suffix}`

  // 1. Dile özel alan varsa onu dön (name, nameEn, nameRu vb.)
  if (row[localizedKey]) return row[localizedKey]

  // 2. Özel durum: tr için localizedKey boştur ama veride nameTr olabilir
  const trKey = `${field}Tr`
  if (language === 'tr' && row[trKey]) return row[trKey]
  
  // 3. İngilizce alan varsa (en dışında bir dilse) onu dön
  const enKey = `${field}En`
  if (language !== 'en' && row[enKey]) return row[enKey]
  
  // 4. Varsayılan (tr) alanı dön
  if (row[field]) return row[field]
  
  return '-'
}

const general = {
  notificationError,
  messageSuccess,
  generateRandomString,
  isNullOrEmpty,
  removeNullValues,
  addOptionalFields,
  confirmDelete,
  showDeleteSuccess,
  formatDate,
  getLocaleValue
}

export default general


